import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'

import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress, Divider, FormControlLabel, Skeleton, Stack, Switch, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import axios from 'axios'

import { custdevService } from '../../api/services/custdev'

import { draftFrom, rulesFrom } from './custdev-audience'

import type { CustdevDraft as Draft } from './custdev-audience'
import type { CustdevSettings } from '../../api/services/custdev'

const cardSx = {
  p: { xs: 2.5, sm: 3 },
  borderRadius: '18px',
  boxShadow: 'none',
  transition: 'none',
  '&:hover': { boxShadow: 'none' },
}

type ChangeField = (field: keyof Draft, value: string | boolean) => void

function ActivityFields({ draft, disabled, onChange }: { draft: Draft; disabled: boolean; onChange: ChangeField }) {
  return (
    <Stack spacing={2.5}>
      {([
        ['Days since first app launch', 'minDaysSinceFirstLaunch', 'maxDaysSinceFirstLaunch', 36500],
        ['Number of app launches', 'minAppLaunches', 'maxAppLaunches', 1000000000],
      ] as const).map(([label, min, max, limit]) => (
        <Box component="fieldset" key={min} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
          <Typography component="legend" variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>{label}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1.5 }}>
            <TextField fullWidth label="Minimum" type="number" required disabled={disabled} value={draft[min]}
              inputProps={{ min: 0, max: limit, step: 1, 'aria-label': `${label}: minimum` }}
              onChange={event => onChange(min, event.target.value)} />
            <TextField fullWidth label="Maximum" type="number" disabled={disabled} value={draft[max]} placeholder="No limit" slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ min: Number(draft[min]), max: limit, step: 1, 'aria-label': `${label}: maximum` }}
              onChange={event => onChange(max, event.target.value)} />
          </Box>
        </Box>
      ))}
      <Typography variant="body2" color="text.secondary">Leave maximums blank for no upper limit.</Typography>
    </Stack>
  )
}

function AudienceCard({ title, description, icon, enabled, busy, onChange, children }: {
  title: string
  description: string
  icon: ReactNode
  enabled: boolean
  busy: boolean
  onChange: (enabled: boolean) => void
  children: ReactNode
}) {
  return (
    <Card component="section" aria-label={title} sx={{ ...cardSx, height: '100%', borderColor: theme => enabled ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, flexShrink: 0, borderRadius: '12px', color: enabled ? 'primary.dark' : 'text.secondary', bgcolor: theme => alpha(theme.palette.primary.main, enabled ? 0.1 : 0.04) }}>{icon}</Box>
        <Typography component="h3" variant="h6" sx={{ flex: 1, lineHeight: 1.3 }}>{title}</Typography>
        <Switch checked={enabled} disabled={busy} slotProps={{ input: { 'aria-label': title, role: 'switch' } }} onChange={event => onChange(event.target.checked)} sx={{ mr: -1 }} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textWrap: 'pretty' }}>{description}</Typography>
      {children}
    </Card>
  )
}

const errorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409) return 'Someone changed these settings. Reload to see their changes before saving again.'
    if (error.response?.status === 404) return 'Interview audience settings are not available on this server yet.'
    const message: unknown = error.response?.data?.message
    if (typeof message === 'string') return message
    if (Array.isArray(message) && message.every(item => typeof item === 'string')) return message.join('. ')
  }
  return 'Could not load or save invitation settings. Try reloading.'
}

export default function CustdevForm() {
  const [settings, setSettings] = useState<CustdevSettings | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [matchingExpanded, setMatchingExpanded] = useState(false)

  const accept = (value: CustdevSettings) => {
    setSettings(value)
    setDraft(draftFrom(value))
    setMatchingExpanded(!value.rules.audiences)
  }
  const load = useCallback(async () => {
    setBusy(true); setError(''); setSaved(false)
    try { accept(await custdevService.get()) }
    catch (err) { setError(errorMessage(err)) }
    finally { setBusy(false) }
  }, [])
  useEffect(() => { void load() }, [load])

  const change: ChangeField = (field, value) => {
    setSaved(false)
    setDraft(current => current ? { ...current, [field]: value } : current)
  }
  const dirty = !!settings && !!draft && (settings.experienceCallEnabled !== draft.experienceCallEnabled ||
    JSON.stringify(rulesFrom(draftFrom(settings))) !== JSON.stringify(rulesFrom(draft)))

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!settings || !draft || busy || !dirty) return
    setBusy(true); setError(''); setSaved(false)
    try {
      accept(await custdevService.update({ experienceCallEnabled: draft.experienceCallEnabled, rules: rulesFrom(draft) }, settings.revision))
      setSaved(true)
    } catch (err) { setError(errorMessage(err)) }
    finally { setBusy(false) }
  }

  const advertisingField = (segmented: boolean) => (
    <TextField fullWidth label="Advertising name contains" multiline minRows={2} disabled={busy || (segmented && !draft?.pelvicAds)}
      required={segmented && !!draft?.pelvicAds} value={draft?.adKeywords ?? ''} placeholder="Pelvic, Vagus"
      helperText={segmented ? 'Match any keyword, ignoring case. Separate keywords with commas. Users without matching ad attribution are excluded.' : 'Any keyword matches, ignoring case. Leave blank for all sources; users without an ad source are excluded when a filter is set.'}
      onChange={event => change('adKeywords', event.target.value)} />
  )

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Invitation settings saved. Changes apply on users’ next app launch.</Alert>}
      {!draft ? (
        <Card sx={cardSx}>
          {busy ? <Stack spacing={2}><Skeleton height={48} /><Skeleton height={100} /><Skeleton height={160} /></Stack>
            : <Button variant="outlined" onClick={() => void load()}>Reload settings</Button>}
        </Card>
      ) : (
        <Box component="form" onSubmit={save} aria-busy={busy} sx={{ display: 'flex', flexDirection: 'column', gap: 3,
          '& .MuiButton-root': { minHeight: 40, transition: 'background-color 150ms ease, box-shadow 150ms ease', '&:hover': { transform: 'none' } },
          '@media (prefers-reduced-motion: reduce)': { '& .MuiButton-root': { transition: 'none' } },
        }}>
          <Card component="section" aria-labelledby="invitation-status-title" sx={cardSx}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                  <Typography id="invitation-status-title" component="h2" variant="h6">Interview invitations</Typography>
                  <Chip size="small" label={draft.experienceCallEnabled ? 'On' : 'Off'} color={draft.experienceCallEnabled ? 'success' : 'default'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">Applies on the next launch in app 1.0.49 or later.</Typography>
              </Box>
              <Switch checked={draft.experienceCallEnabled} disabled={busy} slotProps={{ input: { 'aria-label': 'Enable interview invitations in app 1.0.49+', role: 'switch' } }} onChange={event => change('experienceCallEnabled', event.target.checked)} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>The invitation setting for app 1.0.48 is independent.</Typography>
          </Card>

          <Box component="section" aria-labelledby="audiences-title">
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.75 }}>
              <Typography id="audiences-title" component="h2" variant="h6">Who receives an invitation</Typography>
              {!draft.separateAudiences && <Chip size="small" label="Legacy mode" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {draft.separateAudiences ? 'Users qualify by matching any enabled audience. Each card has its own rules.' : 'Users must match all of the conditions below. You can change this in Audience matching.'}
            </Typography>
            {draft.separateAudiences ? (
              <Stack spacing={2}>
                {!draft.powerUsers && !draft.pelvicAds && !draft.addonBuyers && <Alert severity="info">No audience is enabled. Nobody will receive an invitation.</Alert>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)' }, gap: 2 }}>
                  <Box sx={{ gridRow: { lg: 'span 2' } }}>
                    <AudienceCard title="Power users" description="Set time and activity limits for this audience only." icon={<PeopleOutlineIcon />} enabled={draft.powerUsers} busy={busy} onChange={enabled => change('powerUsers', enabled)}>
                      <ActivityFields draft={draft} disabled={busy || !draft.powerUsers} onChange={change} />
                    </AudienceCard>
                  </Box>
                  <AudienceCard title="Pelvic ad users" description="Include users from matching ads, regardless of activity or add-on purchases." icon={<CampaignOutlinedIcon />} enabled={draft.pelvicAds} busy={busy} onChange={enabled => change('pelvicAds', enabled)}>
                    {advertisingField(true)}
                  </AudienceCard>
                  <AudienceCard title="Add-on buyers" description="Include users who bought either of these add-ons, regardless of ad source or activity." icon={<ShoppingBagOutlinedIcon />} enabled={draft.addonBuyers} busy={busy} onChange={enabled => change('addonBuyers', enabled)}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip label="Wellbeing report" size="small" variant="outlined" />
                      <Chip label="Meal plan" size="small" variant="outlined" />
                    </Stack>
                  </AudienceCard>
                </Box>
              </Stack>
            ) : (
              <Card component="section" aria-label="Combined audience conditions" sx={cardSx}>
                <Stack spacing={3}>
                  <Typography component="h3" variant="h6">Combined conditions</Typography>
                  <ActivityFields draft={draft} disabled={busy} onChange={change} />
                  {advertisingField(false)}
                </Stack>
              </Card>
            )}
          </Box>

          <Card component="section" aria-labelledby="shared-settings-title" sx={cardSx}>
            <Typography id="shared-settings-title" component="h2" variant="h6" sx={{ mb: 0.75 }}>Shared settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>App versions, booking and interview round apply to all audiences.</Typography>
            <Stack spacing={3}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label="Minimum app version" required disabled={busy} value={draft.minAppVersion} helperText="1.0.49 or later" onChange={event => change('minAppVersion', event.target.value)} />
                <TextField fullWidth label="Maximum app version" disabled={busy} value={draft.maxAppVersion} placeholder="No limit" slotProps={{ inputLabel: { shrink: true } }} helperText="Inclusive, e.g. 1.0.50" onChange={event => change('maxAppVersion', event.target.value)} />
              </Box>
              <Divider />
              <TextField fullWidth label="Booking link" type="url" required disabled={busy} value={draft.bookingUrl} helperText="HTTPS booking page opened by Book your spot." onChange={event => change('bookingUrl', event.target.value)} />
              <TextField fullWidth label="Interview campaign ID" disabled={busy} value={draft.campaignId} inputProps={{ maxLength: 64, pattern: '[a-z0-9][a-z0-9_\\-]{0,63}' }} placeholder="research-october-2026" helperText="Use 1 to 64 lowercase letters, digits, hyphens or underscores, starting with a letter or digit. Leave blank for the original one-time invitation. Use a new ID only for a new interview round; campaign-aware clients may invite previously dismissed users again." onChange={event => change('campaignId', event.target.value)} />
            </Stack>
          </Card>

          <Accordion expanded={matchingExpanded} onChange={(_, expanded) => setMatchingExpanded(expanded)} disableGutters elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid', borderColor: 'divider', borderRadius: '14px !important', '&:before': { display: 'none' }, '&.Mui-expanded': { m: 0 } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="audience-matching-content" id="audience-matching-header" sx={{ px: 2.5, minHeight: 64 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Audience matching</Typography>
                <Typography variant="body2" color="text.secondary">{draft.separateAudiences ? 'Any enabled audience' : 'All conditions (legacy)'}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails id="audience-matching-content" sx={{ px: 2.5, pb: 2.5 }}>
              <FormControlLabel label="Use three independent audiences" control={<Switch checked={draft.separateAudiences} disabled={busy} onChange={event => change('separateAudiences', event.target.checked)} />} />
              <Typography variant="body2" color="text.secondary">When enabled, a user can match any audience. Turn off to use the legacy mode, where all conditions must match.</Typography>
            </AccordionDetails>
          </Accordion>

          <Card component="footer" sx={{ ...cardSx, position: 'sticky', bottom: 16, zIndex: 1, p: 2, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)', '&:hover': { boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
              <Typography variant="body2" color={dirty ? 'text.primary' : 'text.secondary'} role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dirty ? 'warning.dark' : 'success.main', flexShrink: 0 }} />
                {dirty ? 'Unsaved changes' : 'All changes saved'}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button disabled={busy} onClick={() => void load()} sx={{ flex: { xs: 1, sm: 'none' } }}>Reload</Button>
                <Button type="submit" variant="contained" disabled={busy || !dirty} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ flex: { xs: 1, sm: 'none' } }}>Save changes</Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
      )}
    </Stack>
  )
}
